import React, { useEffect, useMemo, useState } from "react";
import { formatCurrency } from "../utils/helpers";
import { budgetApi, ApiGroupBudgetSummary } from "../services/api";
import { useAuth } from "../context/AuthContext";

interface CategorySummaryProps {
  categorySpending: { [key: string]: number };
  categoryBudget: { [key: string]: number };
  budgetSummary?: ApiGroupBudgetSummary | null;
  onBudgetChanged?: () => void;
}

interface BudgetDraftRow {
  draftId: string;
  category_group: string;
  category: string;
  category_budget: string;
}

const buildDraftRows = (
  budgetSummary?: ApiGroupBudgetSummary | null
): BudgetDraftRow[] =>
  budgetSummary?.category_groups.flatMap((group) =>
    group.categories.map((budget) => ({
      draftId: String(budget.id),
      category_group: budget.category_group,
      category: budget.category,
      category_budget: String(budget.category_budget),
    }))
  ) ?? [];

const CategorySummary: React.FC<CategorySummaryProps> = ({
  categorySpending,
  categoryBudget,
  budgetSummary,
  onBudgetChanged,
}) => {
  const { user } = useAuth();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [draftRows, setDraftRows] = useState<BudgetDraftRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const groupOptions = useMemo(
    () =>
      Array.from(
        new Set(
          budgetSummary?.category_groups.map((group) => group.category_group) ?? []
        )
      ),
    [budgetSummary]
  );

  const categoryOptions = useMemo(
    () =>
      Array.from(
        new Set(
          budgetSummary?.category_groups.flatMap((group) =>
            group.categories.map((budget) => budget.category)
          ) ?? []
        )
      ),
    [budgetSummary]
  );

  const allCategories = useMemo(
    () =>
      Array.from(
        new Set([...Object.keys(categoryBudget), ...Object.keys(categorySpending)])
      ).sort((a, b) => a.localeCompare(b, "zh-CN")),
    [categoryBudget, categorySpending]
  );

  const { rows, totalBudgetSum, totalActualSum } = useMemo(() => {
    let totalBudget = 0;
    let totalActual = 0;

    const categoryRows = allCategories
      .map((category) => {
        const budget = categoryBudget[category] || 0;
        const actual = categorySpending[category] || 0;
        return {
          category,
          budget,
          actual,
          difference: actual - budget,
        };
      })
      .filter(({ budget, actual }) => budget > 0 || actual > 0);

    categoryRows.forEach(({ budget, actual }) => {
      totalBudget += budget;
      totalActual += actual;
    });

    return {
      rows: categoryRows,
      totalBudgetSum: totalBudget,
      totalActualSum: totalActual,
    };
  }, [allCategories, categoryBudget, categorySpending]);

  const totalDifference = totalActualSum - totalBudgetSum;

  useEffect(() => {
    if (!isEditModalOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isEditModalOpen]);

  const openEditModal = () => {
    setDraftRows(buildDraftRows(budgetSummary));
    setError(null);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setDraftRows([]);
    setError(null);
  };

  const updateDraftRow = (
    draftId: string,
    field: keyof Omit<BudgetDraftRow, "draftId">,
    value: string
  ) => {
    setDraftRows((current) =>
      current.map((row) =>
        row.draftId === draftId ? { ...row, [field]: value } : row
      )
    );
  };

  const addDraftRow = () => {
    setDraftRows((current) => [
      ...current,
      {
        draftId: `new-${Date.now()}-${current.length}`,
        category_group: groupOptions[0] ?? "",
        category: "",
        category_budget: "0",
      },
    ]);
  };

  const deleteDraftRow = (draftId: string) => {
    setDraftRows((current) => current.filter((row) => row.draftId !== draftId));
  };

  const handleSubmitBudget = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user?.group_id) return;

    const budgets = draftRows.map((row) => ({
      category_group: row.category_group.trim(),
      category: row.category.trim(),
      category_budget: Number(row.category_budget),
    }));

    if (
      budgets.some(
        (budget) =>
          !budget.category_group ||
          !budget.category ||
          Number.isNaN(budget.category_budget) ||
          budget.category_budget < 0
      )
    ) {
      setError("请填写完整的预算组、类别和非负金额。");
      return;
    }

    const uniqueKeys = new Set(
      budgets.map((budget) => `${budget.category_group}\u0000${budget.category}`)
    );
    if (uniqueKeys.size !== budgets.length) {
      setError("预算组和类别不能重复。");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await budgetApi.renewBudgets(user.group_id, budgets);
      closeEditModal();
      onBudgetChanged?.();
    } catch (submitError) {
      console.error("Failed to renew budgets:", submitError);
      setError("提交预算失败，请稍后重试。");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button
          type="button"
          onClick={openEditModal}
          className="px-4 py-1.5 text-sm font-medium rounded-lg border bg-white text-slate-700 border-gray-300 hover:bg-gray-50 transition-colors"
        >
          编辑预算
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                类别
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                预算
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                实际
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                差额
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map(({ category, budget, actual, difference }) => (
              <tr key={category}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {category}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                  {formatCurrency(budget)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                  {formatCurrency(actual)}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
                    difference > 0 ? "text-red-600" : "text-green-600"
                  }`}
                >
                  {formatCurrency(difference)}
                </td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-semibold border-t-2 border-gray-300">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                总计
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                {formatCurrency(totalBudgetSum)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                {formatCurrency(totalActualSum)}
              </td>
              <td
                className={`px-6 py-4 whitespace-nowrap text-sm font-bold text-right ${
                  totalDifference > 0 ? "text-red-600" : "text-green-600"
                }`}
              >
                {formatCurrency(totalDifference)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {isEditModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
        >
          <div
            className="bg-white rounded-xl shadow-lg p-6 w-full max-w-4xl mx-4 max-h-[85vh] flex flex-col"
            onWheel={(event) => event.stopPropagation()}
            onTouchMove={(event) => event.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">编辑预算</h3>
              <button
                type="button"
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="关闭"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmitBudget} className="min-h-0 flex flex-col">
              <div className="overflow-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        预算组
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        类别
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        金额
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        操作
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {draftRows.map((row) => (
                      <tr key={row.draftId}>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            list="budget-group-options"
                            value={row.category_group}
                            onChange={(event) =>
                              updateDraftRow(
                                row.draftId,
                                "category_group",
                                event.target.value
                              )
                            }
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                            required
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            list="budget-category-options"
                            value={row.category}
                            onChange={(event) =>
                              updateDraftRow(
                                row.draftId,
                                "category",
                                event.target.value
                              )
                            }
                            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                            required
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.category_budget}
                            onChange={(event) =>
                              updateDraftRow(
                                row.draftId,
                                "category_budget",
                                event.target.value
                              )
                            }
                            onWheel={(event) => event.currentTarget.blur()}
                            className="block w-32 ml-auto border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2 text-right [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            required
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => deleteDraftRow(row.draftId)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            删除
                          </button>
                        </td>
                      </tr>
                    ))}
                    {draftRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-8 text-center text-sm text-gray-500"
                        >
                          暂无预算项
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <datalist id="budget-group-options">
                {groupOptions.map((group) => (
                  <option key={group} value={group} />
                ))}
              </datalist>
              <datalist id="budget-category-options">
                {categoryOptions.map((category) => (
                  <option key={category} value={category} />
                ))}
              </datalist>

              {error && (
                <p className="text-sm text-red-600 mt-3" role="alert">
                  {error}
                </p>
              )}

              <div className="flex justify-between items-center pt-4">
                <button
                  type="button"
                  onClick={addDraftRow}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  + 添加预算项
                </button>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    disabled={isSubmitting}
                    className="bg-gray-300 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors duration-300 disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-slate-700 text-white font-bold py-2 px-4 rounded-lg hover:bg-slate-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "提交中..." : "提交完整预算"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategorySummary;
